/**
 * Background-resilient upload manager (singleton) — with IndexedDB
 * persistence for survival across app crashes / hard reloads /
 * iOS PWA kill events. (2026-02-19 — iter 169, "true resumable")
 *
 * Why this exists
 * ───────────────
 * Before this, every page kicked off uploads via axios calls bound to
 * its own React component. If the user navigated away mid-upload (very
 * common on mobile when sending 15 photos to a job-card task), the axios
 * promises would still complete IN MEMORY but the post-upload "attach
 * to entity" callback lived inside the unmounted component, so the
 * entity never got the photo. From the user's perspective: "I uploaded
 * 15, only 6 are on the JC."
 *
 * On top of the in-memory queue we now persist every "resumable"
 * upload to IndexedDB. If the visitor hard-refreshes, closes the tab,
 * or iOS axes the PWA process while uploads are queued, the manager
 * rehydrates from IndexedDB on next boot and re-runs them.
 *
 * Resumability contract
 * ─────────────────────
 *   • Caller passes `attach_kind: "jobcard_task_photo"` + a JSON-
 *     serialisable `attach_payload: {jc_id, task_id}`.
 *   • A handler is registered globally via `registerAttachHandler(...)`.
 *   • The manager persists the blob + metadata to IndexedDB.
 *   • On restore, it re-enqueues using the registered handler.
 *
 * Backward compatibility
 * ──────────────────────
 *   • Legacy callers that pass an `attach` closure still work — but
 *     they ARE NOT persisted (closures can't be serialised). They die
 *     on hard reload, same as before. No regression.
 *   • Migrating a call site = adding `attach_kind` + `attach_payload`
 *     + registering a handler. Trivial.
 */

import api from "./api";
import { compressImage } from "./imageCompress";
import {
  persistItem, removePersisted, readAllPersisted, persistedCount, clearAllPersisted,
} from "./uploadPersistence";

// ── Pub-sub plumbing ─────────────────────────────────────────────
let _items = [];
const _subs = new Set();

// ── Attach-handler registry ──────────────────────────────────────
// Maps attach_kind → async (uploadRecord, attach_payload) => void
const _attachHandlers = new Map();

export function registerAttachHandler(kind, fn) {
  if (!kind || typeof fn !== "function") return;
  _attachHandlers.set(kind, fn);
}

// ── Concurrency control ──────────────────────────────────────────
const MAX_CONCURRENT = 2;
function _inflightCount() {
  return _items.filter((i) => i.status === "uploading" || i.status === "attaching" || i.status === "starting").length;
}
function _pumpQueue() {
  while (_inflightCount() < MAX_CONCURRENT) {
    const next = _items.find((i) => i.status === "queued");
    if (!next) return;
    _patch(next.id, { status: "starting" });
    setTimeout(() => _runUpload(next.id), 0);
  }
}

// ── Retry-on-5xx with exponential backoff ───────────────────────
const MAX_RETRIES = 3;
// Iter 231.7 — files at/above this size use the chunked upload path.
// 80 MB lets phone videos comfortably round-trip; below this we use
// the single-shot endpoint (faster, no init/complete dance).
const CHUNKED_UPLOAD_THRESHOLD = 80 * 1024 * 1024;  // 80 MB
const CHUNKED_UPLOAD_CHUNK_SIZE = 5 * 1024 * 1024;  // must match backend
function _isRetryable(err) {
  if (!err) return false;
  const s = err?.response?.status;
  if (s == null) return true; // network error → retry
  return s >= 500 || s === 429;
}
function _delay(ms) { return new Promise((r) => setTimeout(r, ms)); }

function _emit() {
  const snap = _items.map((it) => ({ ...it })); // immutable snapshot
  _subs.forEach((fn) => {
    try { fn(snap); } catch { /* never break the dispatch */ }
  });
}

export function subscribe(fn) {
  _subs.add(fn);
  try { fn(_items.map((i) => ({ ...i }))); } catch { /* noop */ }
  return () => { _subs.delete(fn); };
}

export function getAllUploads() {
  return _items.map((i) => ({ ...i }));
}

export function getUploadsByTarget(targetKind, targetId) {
  return _items
    .filter((i) => i.target_kind === targetKind && i.target_id === targetId)
    .map((i) => ({ ...i }));
}

// ── Internal helpers ─────────────────────────────────────────────
function _kindByMime(mime) {
  if (!mime) return "file";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  return "file";
}

function _newId() {
  return `up-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function _patch(id, patch) {
  const idx = _items.findIndex((i) => i.id === id);
  if (idx < 0) return;
  _items[idx] = { ..._items[idx], ...patch };
  _emit();
}

async function _uploadChunked(id, fileToSend, item) {
  /* Iter 231.7 — chunked upload for big videos.
     1. POST /uploads/chunked/init  → { upload_id, chunk_size }
     2. POST /uploads/chunked/{uid}/chunk?index=N  (per chunk, with retry)
     3. POST /uploads/chunked/{uid}/complete → upload doc
     On any unrecoverable error we DELETE /uploads/chunked/{uid} to
     clean up so we don't leak chunk rows. */
  const sendName = item.filename || (fileToSend && fileToSend.name) || `upload-${Date.now()}.bin`;
  const totalChunks = Math.ceil(fileToSend.size / CHUNKED_UPLOAD_CHUNK_SIZE);
  // Step 1 — init.
  let init;
  try {
    init = await api.post("/uploads/chunked/init", {
      filename: sendName,
      size: fileToSend.size,
      total_chunks: totalChunks,
      content_type: fileToSend.type || "application/octet-stream",
      source: item.source || null,
    });
  } catch (e) {
    throw e;
  }
  const uploadId = init.data.upload_id;
  const chunkSize = init.data.chunk_size || CHUNKED_UPLOAD_CHUNK_SIZE;
  // Step 2 — push every chunk with retry.
  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(fileToSend.size, start + chunkSize);
    const blob = fileToSend.slice(start, end);
    const fd = new FormData();
    fd.append("chunk", blob, `${sendName}.part${i}`);
    let chAttempt = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        await api.post(
          `/uploads/chunked/${uploadId}/chunk?index=${i}`,
          fd,
          {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 0,
            onUploadProgress: (e) => {
              if (!e.total) return;
              const overall = ((start + (e.loaded / e.total) * (end - start)) / fileToSend.size) * 100;
              _patch(id, { progress: Math.min(99, Math.round(overall)) });
            },
          },
        );
        break;
      } catch (e) {
        if (chAttempt < MAX_RETRIES && _isRetryable(e)) {
          const wait = Math.min(7000, 1000 * (2 ** chAttempt) + Math.floor(Math.random() * 250));
          _patch(id, { retry_note: `Chunk ${i + 1}/${totalChunks} retry in ${Math.round(wait / 1000)}s` });
          await _delay(wait);
          chAttempt += 1;
          continue;
        }
        // Unrecoverable — best-effort cleanup, then re-throw.
        try { await api.delete(`/uploads/chunked/${uploadId}`); } catch { /* ignore */ }
        throw e;
      }
    }
  }
  // Step 3 — complete.
  const { data } = await api.post(
    `/uploads/chunked/${uploadId}/complete`,
    null,
    { timeout: 0 },
  );
  return data;
}

async function _runUpload(id) {
  const item = _items.find((i) => i.id === id);
  if (!item || item.status === "uploading") return;
  _patch(id, { status: "uploading", progress: 0, error: null });

  let fileToSend = item.file;
  try {
    const compressed = await compressImage(item.file);
    if (compressed !== item.file) {
      fileToSend = compressed;
      _patch(id, { compressed_size: compressed.size, original_size: item.file.size });
    }
  } catch { /* keep original */ }

  const fd = new FormData();
  // IMPORTANT: when restored from IndexedDB the "file" is a Blob without
  // a name — append with an explicit filename so the backend's
  // file.filename isn't null on restore.
  const sendName = item.filename || (item.file && item.file.name) || `upload-${Date.now()}.bin`;
  fd.append("file", fileToSend, sendName);
  if (item.source) fd.append("source", item.source);

  let attempt = 0;
  let lastErr = null;
  while (attempt <= MAX_RETRIES) {
    try {
      let data;
      // Iter 231.7 — auto-route to chunked endpoint for big files
      // (mostly phone videos). Single-shot path otherwise.
      if (fileToSend.size >= CHUNKED_UPLOAD_THRESHOLD) {
        data = await _uploadChunked(id, fileToSend, item);
      } else {
        let lastTickAt = Date.now();
        let abort = null;
        try { abort = new AbortController(); } catch { /* old browser */ }
        const stallWatcher = setInterval(() => {
          if (Date.now() - lastTickAt > 45000 && abort) {
            try { abort.abort(); } catch { /* ignore */ }
          }
        }, 5000);
        try {
          const resp = await api.post("/uploads", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            signal: abort?.signal,
            onUploadProgress: (e) => {
              lastTickAt = Date.now();
              if (!e.total) return;
              const pct = Math.min(99, Math.round((e.loaded / e.total) * 100));
              _patch(id, { progress: pct, stalled: false });
            },
            timeout: 0,
          });
          data = resp.data;
        } finally {
          clearInterval(stallWatcher);
        }
      }
      const rec = {
        id: data.id,
        filename: data.original_filename,
        content_type: data.content_type,
        size: data.size,
        kind: _kindByMime(data.content_type),
      };
      _patch(id, { status: "attaching", progress: 100, upload_record: rec });

      // Resolve the attach step. Priority:
      //   1. attach_kind + registered handler (resume-safe path)
      //   2. legacy attach closure (in-memory only)
      let attachOk = true;
      let attachErr = null;
      if (item.attach_kind && _attachHandlers.has(item.attach_kind)) {
        try {
          await _attachHandlers.get(item.attach_kind)(rec, item.attach_payload || {});
        } catch (e) {
          attachOk = false;
          attachErr = e;
        }
      } else if (item.attach) {
        try {
          await item.attach(rec);
        } catch (e) {
          attachOk = false;
          attachErr = e;
        }
      }
      if (!attachOk) {
        _patch(id, {
          status: "attach_failed",
          error: _asErrorString(attachErr),
        });
        // We keep the IndexedDB row so the user can retry — retry()
        // re-queues and re-runs the attach handler with the SAME
        // already-uploaded file. Worst case: duplicate dedup'd at the
        // /api/uploads/{fid}/duplicate-check layer.
        _pumpQueue();
        return;
      }

      _patch(id, { status: "done", progress: 100 });
      // Successful terminal — clear persistence.
      if (item._persisted) { try { await removePersisted(id); } catch { /* ignore */ } }
      setTimeout(() => {
        _items = _items.filter((i) => i.id !== id);
        _emit();
      }, 6000);
      _pumpQueue();
      return;
    } catch (e) {
      lastErr = e;
      if (attempt < MAX_RETRIES && _isRetryable(e)) {
        const wait = Math.min(7000, 1000 * (2 ** attempt) + Math.floor(Math.random() * 250));
        const code = e?.response?.status ? `HTTP ${e.response.status}` : "network";
        _patch(id, {
          progress: 0,
          retry_note: `${code} — retry ${attempt + 1}/${MAX_RETRIES} in ${Math.round(wait / 1000)}s`,
        });
        await _delay(wait);
        attempt += 1;
        continue;
      }
      break;
    }
  }

  _patch(id, {
    status: "error",
    error: _asErrorString(lastErr),
    retry_note: null,
  });
  // Persisted items stay in IDB so the next app boot picks them up.
  _pumpQueue();
}

// [Iter 339 — 2026-02-25] Defensive stringifier for upload errors.
// FastAPI validation errors return `detail` as an array of dicts, and
// some other 5xx endpoints wrap it in `{detail: {msg, ...}}`. When
// React tries to render a non-string as a child it throws "Objects
// are not valid as a React child" — which crashed the whole app the
// moment the user opened the failed-uploads dock. Dirk 2026-02-25:
// "every time I click on it, the app crashes".
function _asErrorString(err) {
  if (!err) return "Upload failed.";
  try {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail.slice(0, 240);
    if (detail && typeof detail === "object") {
      // Validation error shape: [{loc, msg, type}, …]
      if (Array.isArray(detail) && detail.length && detail[0]?.msg) {
        return String(detail[0].msg).slice(0, 240);
      }
      // { msg: "..." } / { detail: "..." } fallback.
      if (detail.msg) return String(detail.msg).slice(0, 240);
      return "Server rejected the upload.";
    }
    if (typeof err.message === "string" && err.message.trim()) return err.message.slice(0, 240);
    return "Upload failed.";
  } catch {
    return "Upload failed.";
  }
}

// ── Public API ───────────────────────────────────────────────────

/**
 * enqueue({ file, target_kind, target_id, label, attach | attach_kind+attach_payload })
 *
 * For resume-safe uploads, pass:
 *   • attach_kind: string registered via registerAttachHandler()
 *   • attach_payload: JSON-serialisable object
 *
 * Legacy callers can still pass `attach` as a closure — it just won't
 * survive a hard reload (no persistence).
 */
export function enqueue({
  file, target_kind = null, target_id = null, label = null,
  attach = null, attach_kind = null, attach_payload = null, source = null,
}) {
  if (!file) throw new Error("uploadManager.enqueue: file is required");
  const id = _newId();
  const item = {
    id,
    file,
    filename: file.name,
    size: file.size,
    mime: file.type || "",
    kind: _kindByMime(file.type),
    target_kind,
    target_id,
    label,
    attach,
    attach_kind,
    attach_payload,
    source,
    status: "queued",
    progress: 0,
    error: null,
    upload_record: null,
    created_at: Date.now(),
    _persisted: false,
  };
  _items.push(item);
  _emit();

  // Persist resumable items to IndexedDB BEFORE we start the upload.
  // We only persist when attach_kind is supplied (closures can't be
  // serialised). The blob itself is stored natively by IndexedDB.
  if (attach_kind) {
    item._persisted = true;
    persistItem({
      id,
      blob: file,
      filename: file.name,
      mime: file.type || "",
      size: file.size,
      target_kind, target_id, label, source,
      attach_kind, attach_payload,
      created_at: item.created_at,
      attempt: 0,
    }).catch(() => { /* degrade gracefully */ });
  }

  _pumpQueue();
  return id;
}

export function enqueueAndWait({ file, target_kind, target_id, label, attach, attach_kind, attach_payload, source }) {
  return new Promise((resolve, reject) => {
    const id = enqueue({
      file, target_kind, target_id, label, source, attach_kind, attach_payload,
      attach: attach
        ? async (rec) => { await attach(rec); }
        : null,
    });
    const unsub = subscribe((snap) => {
      const it = snap.find((s) => s.id === id);
      if (!it) {
        unsub();
        return;
      }
      if (it.status === "done") {
        unsub();
        resolve(it.upload_record);
      } else if (it.status === "error" || it.status === "attach_failed") {
        unsub();
        reject(new Error(it.error || "Upload failed."));
      }
    });
  });
}

export function retry(id) {
  const item = _items.find((i) => i.id === id);
  if (!item) return;
  if (item.status === "uploading" || item.status === "attaching") return;
  _patch(id, { status: "queued", error: null, progress: 0, retry_note: null });
  _pumpQueue();
}

export function dismiss(id) {
  const item = _items.find((i) => i.id === id);
  _items = _items.filter((i) => i.id !== id);
  _emit();
  // Also wipe the persistence row so it doesn't resurrect on next boot.
  if (item && item._persisted) {
    try { removePersisted(id); } catch { /* ignore */ }
  }
}

export function activeCount() {
  return _items.filter((i) => i.status !== "done").length;
}

/**
 * Restore persisted uploads from IndexedDB. Called once on app boot
 * (see App.js). Items whose handler isn't registered are kept in IDB
 * for a future restore cycle (e.g. handler hasn't loaded yet).
 *
 * Returns the count of items re-enqueued.
 */
export async function restorePersistedQueue() {
  let rows = [];
  try { rows = await readAllPersisted(); } catch { return 0; }
  if (!rows.length) return 0;

  let restored = 0;
  for (const r of rows) {
    if (!r || !r.blob || !r.id) continue;
    // Already in memory (shouldn't happen unless restore called twice).
    if (_items.some((i) => i.id === r.id)) continue;
    // No registered handler → keep persisted, skip for now.
    if (!r.attach_kind || !_attachHandlers.has(r.attach_kind)) continue;

    const file = r.blob; // Blob from IDB — has .size + .type natively.
    // Pin the original filename so the server still gets it.
    const proxiedFile = file;
    try {
      Object.defineProperty(proxiedFile, "name", { value: r.filename || "upload.bin", configurable: true });
    } catch { /* read-only — fine, we send filename via FormData fallback */ }

    const item = {
      id: r.id,
      file: proxiedFile,
      filename: r.filename,
      size: r.size,
      mime: r.mime || "",
      kind: _kindByMime(r.mime || ""),
      target_kind: r.target_kind,
      target_id: r.target_id,
      label: r.label,
      attach: null,
      attach_kind: r.attach_kind,
      attach_payload: r.attach_payload,
      source: r.source,
      status: "queued",
      progress: 0,
      error: null,
      upload_record: null,
      created_at: r.created_at || Date.now(),
      _persisted: true,
      _restored: true,
    };
    _items.push(item);
    restored += 1;
    // Bump attempt counter in IDB (purely informational; the row is
    // wiped on success). This protects against a corrupt row infinite-
    // looping us — handlers should be idempotent (upload dedup handles
    // duplicate /api/uploads calls).
    try {
      await persistItem({ ...r, attempt: (r.attempt || 0) + 1 });
    } catch { /* ignore */ }
  }
  if (restored > 0) {
    _emit();
    _pumpQueue();
  }
  return restored;
}

/** Diagnostic — number of rows persisted to IDB (terminal+pending). */
export async function getPersistedCount() {
  return await persistedCount();
}

/** Diagnostic / settings — wipe every persisted upload. */
export async function clearPersisted() {
  await clearAllPersisted();
}

// ── Browser-leave safety ─────────────────────────────────────────
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => {
    const inFlight = _items.filter((i) => i.status === "uploading" || i.status === "queued" || i.status === "starting" || i.status === "attaching");
    if (inFlight.length > 0) {
      e.preventDefault();
      e.returnValue = `${inFlight.length} upload(s) still in progress.`;
      return e.returnValue;
    }
    return undefined;
  });
}

const uploadManager = {
  enqueue, enqueueAndWait, retry, dismiss, subscribe,
  getAllUploads, getUploadsByTarget, activeCount,
  registerAttachHandler, restorePersistedQueue,
  getPersistedCount, clearPersisted,
};
export default uploadManager;
